import { useState, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';

import type { ComponentsAccordionItem, ComponentsAccordionItemStyle, Media } from 'queries/graphql';

import Image from 'next/image';

import { useTextFontOverrides, useHeadingFontOverrides } from 'utils/font-override';
import { buildImgProps } from 'utils/media';
import { buildCssVar } from 'utils/style-override';

import styles from './styles.module.scss';

const MarkdownText = dynamic(() => import('generic/MarkdownText'));

export default function AccordionItem({
  title,
  text,
  image,
  itemStyle,
}: ComponentsAccordionItem & {
  itemStyle?: ComponentsAccordionItemStyle;
}) {
  const { textColor, linkTextColor, backgroundColor, headingFont, textFont } = itemStyle || {};

  const [isOpen, setIsOpen] = useState(false);
  const handleClick = useCallback(() => setIsOpen(!isOpen), [isOpen]);

  const headingFontOverrides = useHeadingFontOverrides(headingFont);
  const textFontOverrides = useTextFontOverrides(textFont);

  return (
    <div className={`root ${styles.container}`}>
      <style jsx>{`
        .root {
          ${buildCssVar('--text-color', textColor, 'var(--text-color-dark)')}
          ${buildCssVar('--background-color', backgroundColor, 'var(--secondary-color)')}
          ${linkTextColor ? buildCssVar('--link-text-color', linkTextColor) : ''}
          ${headingFontOverrides ?? ''}
          ${textFontOverrides ?? ''}
        }
      `}</style>

      <button onClick={handleClick} className={styles.expanding_btn}>
        <header>
          <h1 className={styles.heading}>{title}</h1>

          {!isOpen ? (
            <FaAngleDown className={`${styles.dropdown_icon}`} />
          ) : (
            <FaAngleUp className={`${styles.dropdown_icon}`} />
          )}
        </header>
      </button>
      <div className={`${styles.contents} ${isOpen ? styles.open : styles.closed}`}>
        <AccordionItemContent text={text} image={image} />
      </div>
    </div>
  );
}

function AccordionItemContent({ text, image }: { text: string; image: Media }) {
  if (image) {
    return <Image {...buildImgProps(image)} alt={text} />;
  }

  return <MarkdownText text={text} />;
}
